import { useEffect, useState } from "react";
import {
  Link,
  Navigate,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { toast } from "sonner";

import {
  ApiError,
  auth as authApi,
  publicApi,
  type PromoLookup,
} from "../../lib/api";
import { useSeo } from "../../lib/useSeo";
import { trackConversion } from "../../lib/analytics";
import { withUtm } from "../../lib/utm";
import {
  isOperatorAuthenticated,
  setOperatorRefreshToken,
  setOperatorToken,
} from "../../lib/auth";
import { Btn } from "../../lib/Btn";
import { Card } from "../../lib/Card";
import { Icon } from "../../lib/Icon";
import { Input } from "../../lib/Input";
import { US_STATES } from "../../lib/usStates";
import { Pill } from "../../lib/Pill";
import { Slats } from "../../lib/Slats";
import { Stepper } from "../../lib/Stepper";

// Two-step wizard: account + first property. Plan / CSV import / access
// integration all land inside the operator dashboard once they're in —
// we never want a signup step where the user clicks "Continue" and nothing
// actually happens.
const STEPS = ["Operator", "Property"];

type Vertical =
  | "multifamily"
  | "self_storage"
  | "parking"
  | "vehicle_storage";

const VERTICAL_OPTIONS: Array<{
  key: Vertical;
  label: string;
  blurb: string;
  examplePropertyName: string;
  exampleSubdomain: string;
  customerNoun: string;
}> = [
  {
    key: "multifamily",
    label: "Multifamily",
    blurb: "Apartments, condos, senior living.",
    examplePropertyName: "Maple Grove Apartments",
    exampleSubdomain: "maple-grove",
    customerNoun: "Residents",
  },
  {
    key: "self_storage",
    label: "Self-storage",
    blurb: "Self-storage facilities — drive-up, climate, or hybrid.",
    examplePropertyName: "Sunset Self Storage",
    exampleSubdomain: "sunset",
    customerNoun: "Tenants",
  },
  {
    key: "parking",
    label: "Parking + lots",
    blurb: "Monthly permits, transient, retail validation.",
    examplePropertyName: "Downtown Garage",
    exampleSubdomain: "downtown-garage",
    customerNoun: "Parkers",
  },
  {
    key: "vehicle_storage",
    label: "RV + boat / marinas",
    blurb: "Marinas, dry stacks, RV lots.",
    examplePropertyName: "Harbor Point Marina",
    exampleSubdomain: "harbor-point",
    customerNoun: "Boaters",
  },
];

interface OperatorForm {
  name: string;
  email: string;
  password: string;
  phone: string;
}

interface FacilityForm {
  name: string;
  address: string;
  city: string;
  state: string;
  zip_code: string;
  slug: string;
  facility_type: Vertical;
}

export function SignupPage() {
  useSeo({
    title: "Sign up — NestPlot · 30-day free trial",
    description: "Start your 30-day NestPlot trial. No card required.",
    path: "/signup",
  });
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  // Already signed in? They probably hit Sign up by accident — bounce
  // them into their dashboard rather than letting them create a new
  // account on top of the existing session.
  if (isOperatorAuthenticated()) {
    return <Navigate to="/app/today" replace />;
  }
  // Step 1 fields may arrive prefilled from the landing-page hero
  // signup card (HeroSignupCard sets location.state.op when it hands
  // off). If all four are populated, jump straight to step 2 so the
  // prospect doesn't re-type them.
  const prefilled = (location.state ?? null) as {
    op?: Partial<OperatorForm>;
    facility_type?: Vertical;
  } | null;
  const prefilledOp = prefilled?.op ?? {};
  const haveFullStep1 = !!(
    prefilledOp.name &&
    prefilledOp.email &&
    prefilledOp.password
  );
  const [step, setStep] = useState(haveFullStep1 ? 1 : 0);
  const [op, setOp] = useState<OperatorForm>({
    name: prefilledOp.name ?? "",
    email: prefilledOp.email ?? "",
    password: prefilledOp.password ?? "",
    phone: prefilledOp.phone ?? "",
  });
  const [promoCode, setPromoCode] = useState(() => params.get("promo") ?? "");
  const [promo, setPromo] = useState<PromoLookup | null>(null);
  const [promoStatus, setPromoStatus] = useState<"idle" | "checking" | "applied" | "invalid">("idle");

  useEffect(() => {
    const code = promoCode.trim();
    if (!code) {
      setPromo(null);
      setPromoStatus("idle");
      return;
    }
    let cancelled = false;
    setPromoStatus("checking");
    const handle = window.setTimeout(async () => {
      try {
        const result = await publicApi.lookupPromo(code);
        if (cancelled) return;
        setPromo(result);
        setPromoStatus("applied");
      } catch {
        if (cancelled) return;
        setPromo(null);
        setPromoStatus("invalid");
      }
    }, 300);
    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [promoCode]);
  const [fac, setFac] = useState<FacilityForm>({
    name: "",
    address: "",
    city: "",
    // Force the operator to pick their state — defaulting to "IL"
    // silently shipped a prefilled value an operator outside Illinois
    // would have to remember to change before saving. Empty string +
    // a placeholder option on the <select> makes the prompt explicit.
    state: "",
    zip_code: "",
    slug: "",
    // Landing pages can suggest the vertical they arrived from
    // (/for-marina → marina) via location.state.facility_type.
    facility_type: prefilled?.facility_type ?? "multifamily",
  });
  const verticalMeta =
    VERTICAL_OPTIONS.find((v) => v.key === fac.facility_type) ??
    VERTICAL_OPTIONS[0];
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  async function handleContinue() {
    if (step === 0) {
      // Validate the operator block but do NOT create the account yet —
      // we POST /auth/signup atomically with the facility block on step 2
      // so an abandoned wizard doesn't leave an orphan user in the DB.
      if (!op.name.trim()) {
        setError("Your name is required");
        return;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(op.email.trim())) {
        setError("A valid email is required");
        return;
      }
      if (op.password.length < 8) {
        setError("Password must be at least 8 characters");
        return;
      }
      setError(null);
      setStep(1);
      return;
    }
    if (step === 1) {
      if (!fac.name || !fac.slug) {
        setError("Property name and subdomain are required");
        return;
      }
      if (!fac.address || !fac.city || !fac.state || !fac.zip_code) {
        setError("Property address, city, state, and ZIP are required");
        return;
      }
      setSubmitting(true);
      setError(null);
      try {
        // One conversion id, shared between the request body (→ the
        // server-side Meta CAPI event) and the browser pixel below, so
        // Meta dedupes the pair.
        const eventId = crypto.randomUUID();
        const response = await authApi.signup(withUtm({
          name: op.name,
          email: op.email,
          password: op.password,
          phone: op.phone || undefined,
          promo_code: promo ? promo.code : undefined,
          event_id: eventId,
          facility: {
            slug: fac.slug,
            name: fac.name,
            address: fac.address,
            city: fac.city,
            state: fac.state,
            zip_code: fac.zip_code,
            facility_type: fac.facility_type,
          },
        }));
        // GTM conversion event — fired BEFORE the navigate() to
        // app.nestplot.com so it lands while the prospect is still on
        // the marketing surface (where GTM is loaded). Email passes
        // through for Google Ads Enhanced Conversions (hashed in the tag
        // layer); event_id ties this browser fire to the server CAPI.
        trackConversion("signup_completed", {
          email: op.email,
          phone: op.phone || undefined,
          vertical: fac.facility_type,
          event_id: eventId,
        });
        if (response.verification_required) {
          // Most signups land here: API created the operator, sent the
          // verify email, and gave us no tokens. The verify-email
          // screen displays which inbox to check and offers a resend.
          navigate(
            `/verify-pending?email=${encodeURIComponent(response.email ?? op.email)}`,
          );
          return;
        }
        setOperatorToken(response.access_token);
        if ("refresh_token" in response && response.refresh_token) {
          setOperatorRefreshToken(response.refresh_token);
        }
        const trialDays = promo?.trial_extension_days ?? 30;
        toast.success(`Trial started · ${trialDays} days, no card`);
        navigate("/app/today");
      } catch (err) {
        if (err instanceof ApiError && err.status === 409) {
          const detail =
            typeof err.payload === "object" &&
            err.payload &&
            "detail" in err.payload
              ? String((err.payload as { detail: string }).detail)
              : "";
          if (detail.includes("slug")) {
            setError("That subdomain is taken. Try another.");
            setStep(1);
          } else {
            setError("That email is already registered. Try signing in.");
            setStep(0);
          }
        } else {
          setError("Signup failed — check your inputs and try again");
        }
      } finally {
        setSubmitting(false);
      }
      return;
    }
    navigate("/app/today");
  }

  return (
    <div className="mx-auto max-w-[1080px] px-5 lg:px-8 py-12">
      <Link
        to="/"
        className="inline-flex items-center gap-2 text-sub text-[13px] font-semibold hover:text-ink mb-3"
      >
        <Icon name="arrow-left" size={14} /> Home
      </Link>
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-6">
        <div>
          <div className="font-mono text-[11px] text-sub tracking-[0.14em] uppercase">
            Start free · {promo?.trial_extension_days ?? 30} days
          </div>
          <h1 className="t-display-l mt-1">Get set up</h1>
          <div className="text-sub text-sm mt-2">
            Already have an account?{" "}
            <Link
              to={`/signin${promoCode.trim() ? `?promo=${encodeURIComponent(promoCode.trim())}` : ""}`}
              className="text-accent font-semibold hover:underline"
            >
              Sign in
            </Link>
          </div>
        </div>
        <Pill tone="ok" size="sm">
          No card required
        </Pill>
      </div>

      <Card>
        <div className="px-6 py-5 border-b border-line">
          <Stepper steps={STEPS} current={step} />
        </div>
        <div className="grid lg:grid-cols-[1.4fr_1fr] gap-0">
          <div className="p-6 border-r border-line space-y-4">
            {step === 0 && (
              <>
                <SectionTitle>Operator info</SectionTitle>
                <div className="grid md:grid-cols-2 gap-4">
                  <Input
                    label="Your name"
                    placeholder="Pat Casey"
                    value={op.name}
                    onChange={(e) => setOp({ ...op, name: e.target.value })}
                    required
                  />
                  <Input
                    label="Phone"
                    placeholder="(217) 555-0184"
                    value={op.phone}
                    onChange={(e) => setOp({ ...op, phone: e.target.value })}
                  />
                  <Input
                    label="Email"
                    type="email"
                    placeholder="you@operator.com"
                    value={op.email}
                    onChange={(e) => setOp({ ...op, email: e.target.value })}
                    required
                  />
                  <Input
                    label="Password"
                    type={showPassword ? "text" : "password"}
                    placeholder="At least 8 characters"
                    value={op.password}
                    onChange={(e) => setOp({ ...op, password: e.target.value })}
                    error={error ?? undefined}
                    required
                    suffix={
                      <button
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        className="text-sub hover:text-ink"
                        aria-label={showPassword ? "Hide password" : "Show password"}
                      >
                        {showPassword ? "Hide" : "Show"}
                      </button>
                    }
                  />
                </div>
                <div className="pt-2">
                  <Input
                    label="Promo code (optional)"
                    placeholder="Enter code"
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                  />
                  {promoStatus === "checking" && (
                    <div className="text-xs text-sub mt-1">Checking code…</div>
                  )}
                  {promoStatus === "applied" && promo && (
                    <div className="mt-2 px-3 py-2 rounded-[8px] bg-accent/10 border border-accent/30 text-sm">
                      <span className="font-bold text-accent uppercase font-mono text-[11px] tracking-[0.12em] mr-2">
                        Applied
                      </span>
                      {promo.description ||
                        `${promo.trial_extension_days}-day trial${promo.discount_percent ? ` · ${promo.discount_percent}% off` : ""}`}
                    </div>
                  )}
                  {promoStatus === "invalid" && (
                    <div className="text-xs text-alert mt-1">
                      That code isn't valid right now. You can leave it blank to continue.
                    </div>
                  )}
                </div>
              </>
            )}
            {step === 1 && (
              <>
                <SectionTitle>Property</SectionTitle>
                <p className="text-sub text-sm">
                  Pick the vertical for your first property and fill in the
                  basics. You can add more properties (in the same vertical or
                  others) from Settings after you're in.
                </p>
                <div>
                  <div className="font-mono text-[11px] text-sub tracking-[0.14em] uppercase mb-2">
                    Vertical
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {VERTICAL_OPTIONS.map((v) => {
                      const selected = fac.facility_type === v.key;
                      return (
                        <button
                          key={v.key}
                          type="button"
                          onClick={() => setFac({ ...fac, facility_type: v.key })}
                          className={
                            "text-left rounded-md border px-3 py-3 transition-colors " +
                            (selected
                              ? "border-accent bg-accent/5"
                              : "border-line hover:border-line-strong")
                          }
                          aria-pressed={selected}
                        >
                          <div className="font-display font-extrabold text-[14px] uppercase tracking-[-0.005em]">
                            {v.label}
                          </div>
                          <div className="text-sub text-[12px] mt-1">{v.blurb}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>
                <Input
                  label="Property name"
                  placeholder={verticalMeta.examplePropertyName}
                  value={fac.name}
                  onChange={(e) => setFac({ ...fac, name: e.target.value })}
                  required
                />
                <Input
                  label="Address"
                  placeholder="1842 Pine St"
                  value={fac.address}
                  onChange={(e) => setFac({ ...fac, address: e.target.value })}
                />
                <div className="grid md:grid-cols-3 gap-4">
                  <Input
                    label="City"
                    placeholder="Springfield"
                    value={fac.city}
                    onChange={(e) => setFac({ ...fac, city: e.target.value })}
                  />
                  <div>
                    <label className="block font-mono text-[11px] text-sub uppercase tracking-[0.08em] mb-[6px]">
                      State
                    </label>
                    <select
                      value={fac.state}
                      onChange={(e) => setFac({ ...fac, state: e.target.value })}
                      required
                      className="block w-full bg-paper border-[1.5px] border-line-strong rounded-md px-3 py-[10px] text-[14px] font-medium focus:outline-none focus:border-accent"
                    >
                      <option value="" disabled>
                        Pick a state…
                      </option>
                      {US_STATES.map((s) => (
                        <option key={s.code} value={s.code}>
                          {s.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <Input
                    label="Zip"
                    placeholder="62704"
                    value={fac.zip_code}
                    onChange={(e) => setFac({ ...fac, zip_code: e.target.value })}
                  />
                </div>
                <Input
                  label="Public sign-up subdomain"
                  placeholder={verticalMeta.exampleSubdomain}
                  hint={`${verticalMeta.customerNoun} will reserve at https://[slug].nestplot.com or your own configured domain`}
                  prefix="https://"
                  suffix=".nestplot.com"
                  value={fac.slug}
                  onChange={(e) =>
                    setFac({ ...fac, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") })
                  }
                  error={error ?? undefined}
                  required
                />
              </>
            )}
            {/* Plan / CSV import / gate are configured from the dashboard
                after signup, so we don't dead-end users on steps that don't
                actually persist. */}
          </div>

          <div className="bg-surface p-6">
            <Slats color="var(--accent)" height={10} bands={4} style={{ width: 48, marginBottom: 14 }} />
            <div className="font-mono text-[11px] text-sub tracking-[0.14em] uppercase">
              Summary
            </div>
            <ul className="mt-4 space-y-3 text-sm">
              <SummaryRow
                label="Trial"
                value={`${promo?.trial_extension_days ?? 30} days · no card`}
              />
              {promo?.discount_percent ? (
                <SummaryRow
                  label="Discount"
                  value={`${promo.discount_percent}% off for 1 year`}
                />
              ) : null}
              <SummaryRow
                label="Vertical"
                value={step >= 1 ? verticalMeta.label : "Pick on step 2"}
              />
              <SummaryRow label="Plan" value="Pick from dashboard" />
              <SummaryRow label="Roster import" value="Pick from dashboard" />
              <SummaryRow label="Access vendor" value="Pick from dashboard" />
              <SummaryRow label="Operator" value={op.email || "—"} />
            </ul>
            <div className="mt-5 text-[12px] text-sub leading-relaxed">
              After you create your property, you'll land in your dashboard
              with shortcuts for the plan, roster CSV import, and access-vendor
              connection. None of that has to happen at signup.
            </div>
          </div>
        </div>
        {error && (
          <div
            role="alert"
            className="mx-6 mb-4 mt-2 bg-alert-soft border border-alert/30 text-alert text-sm px-3 py-2 rounded-md"
          >
            {error}
          </div>
        )}
        <div className="flex justify-between items-center px-6 py-4 border-t border-line bg-paper">
          <Btn
            kind="ghost"
            size="sm"
            icon={<Icon name="arrow-left" size={14} />}
            onClick={() => setStep(Math.max(0, step - 1))}
            disabled={step === 0 || submitting}
          >
            Back
          </Btn>
          <Btn
            kind="primary"
            size="md"
            iconRight={<Icon name="arrow-right" size={14} />}
            onClick={handleContinue}
            disabled={submitting}
          >
            {submitting ? "Creating account…" : step === STEPS.length - 1 ? "Finish · open dashboard" : "Continue"}
          </Btn>
        </div>
      </Card>
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <div className="font-display font-extrabold text-[20px] uppercase tracking-[-0.01em]">
      {children}
    </div>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <li className="flex justify-between">
      <span className="text-sub">{label}</span>
      <span className="font-medium">{value}</span>
    </li>
  );
}
